import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  HomeIcon,
  UserGroupIcon,
  ChartBarIcon,
  ClockIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ShieldCheckIcon,
  DocumentMagnifyingGlassIcon,
  DocumentTextIcon,
  BriefcaseIcon,
  FlagIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import type { Notification } from "@/shared/ui/types/NotificationCenter.types";
import { NotificationCenter } from "@/shared/ui";
import { useNotifications } from "../hooks/useNotifications";
import { cn } from "../lib/utils";

interface SidebarItemProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  href: string;
  isActive?: boolean;
  onClick?: () => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  icon: Icon,
  label,
  isActive,
  onClick,
}) => {
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ x: 4 }}
      whileTap={{ scale: 0.98 }}
      className={cn(
        "w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200",
        isActive
          ? "bg-tiber-500 text-white shadow-md"
          : "text-juridico hover:bg-romance-50",
      )}
    >
      <Icon className="w-5 h-5" />
      <span className="font-medium">{label}</span>
    </motion.button>
  );
};

export const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { profile, signOut, user, adminRole } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Notifications
  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
    markAsRead,
    markAllAsRead,
    deleteNotif,
  } = useNotifications(user?.id);

  const handleNotificationClick = (notification: Notification) => {
    if (notification.link) {
      navigate(notification.link);
    }
  };

  // Admin sidebar items
  const sidebarItems: SidebarItemProps[] = [
    {
      icon: HomeIcon,
      label: t("admin.sidebar.dashboard"),
      href: "/admin/dashboard",
    },
    {
      icon: UserGroupIcon,
      label: t("admin.sidebar.users"),
      href: "/admin/users",
    },
    {
      icon: DocumentMagnifyingGlassIcon,
      label: t("admin.sidebar.exequatur"),
      href: "/admin/exequatur",
    },
    {
      icon: BriefcaseIcon,
      label: t("admin.sidebar.cases"),
      href: "/admin/cases",
    },
    {
      icon: DocumentTextIcon,
      label: t("admin.sidebar.documentsModeration"),
      href: "/admin/documents-moderation",
    },
    // {
    //   icon: BookOpenIcon,
    //   label: t("admin.sidebar.materials"),
    //   href: "/admin/materials",
    // },
    {
      icon: ChartBarIcon,
      label: t("admin.sidebar.metrics"),
      href: "/admin/metrics",
    },
    {
      icon: ClockIcon,
      label: t("admin.sidebar.activity"),
      href: "/admin/activity",
    },
    {
      icon: FlagIcon,
      label: t("admin.sidebar.reports"),
      href: "/admin/reports",
    },
    {
      icon: Cog6ToothIcon,
      label: t("admin.sidebar.settings"),
      href: "/admin/settings",
    },
  ];

  const handleNavigation = (href: string) => {
    navigate(href);
    setIsSidebarOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/auth");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const getAdminRoleBadge = () => {
    const roleColors: Record<string, string> = {
      super_admin: "bg-red-100 text-red-800 border-red-200",
      admin: "bg-tiber-100 text-tiber-800 border-tiber-200",
      moderator: "bg-atlantis-100 text-atlantis-800 border-atlantis-200",
    };

    const roleKey = adminRole || "admin";
    const roleLabel = t(`admin.roles.${roleKey}`);

    return (
      <span
        className={cn(
          "px-2 py-1 text-xs font-bold rounded-full border text-tiber-900",
          roleColors[roleKey] || roleColors.admin,
        )}
      >
        {roleLabel}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-nieve">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-tiber-200 z-30">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Left section */}
          <div className="flex items-center space-x-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-romance-50 transition-colors"
            >
              <Bars3Icon className="w-6 h-6 text-juridico" />
            </button>

            {/* Logo and title */}
            <div className="flex items-center space-x-3">
              <ShieldCheckIcon className="w-8 h-8 text-tiber-500 hidden lg:block" />
              <div>
                <h1 className="text-lg font-display font-bold text-juridico">
                  {t("admin.header.title")}
                </h1>
                <p className="text-xs text-juridico/60 hidden sm:block">
                  {profile?.email}
                </p>
              </div>
            </div>

            {/* Admin role badge */}
            <div className="hidden md:block">{getAdminRoleBadge()}</div>
          </div>

          {/* Right section */}
          <div className="flex items-center space-x-2">
            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Notifications */}
            <NotificationCenter
              notifications={notifications}
              unreadCount={unreadCount}
              loading={notificationsLoading}
              onNotificationClick={handleNotificationClick}
              onMarkAsRead={markAsRead}
              onMarkAllAsRead={markAllAsRead}
              onDelete={deleteNotif}
            />

            {/* User avatar */}
            <div className="flex items-center space-x-2 pl-2 border-l border-tiber-200">
              <div className="w-8 h-8 rounded-full bg-tiber-500/10 flex items-center justify-center">
                <span className="text-sm font-bold text-tiber-500">
                  {profile?.full_name?.charAt(0).toUpperCase() || "A"}
                </span>
              </div>
              <div className="hidden lg:block">
                <p className="text-sm font-medium text-juridico">
                  {profile?.full_name || t("admin.roles.admin")}
                </p>
                <p className="text-xs text-juridico/60">
                  {t(`admin.roles.${adminRole || "admin"}`)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar - Desktop */}
      <aside className="hidden lg:block fixed left-0 top-16 bottom-0 w-64 bg-white border-r border-tiber-200 overflow-y-auto z-20">
        <div className="p-4 space-y-2">
          {sidebarItems.map((item) => (
            <SidebarItem
              key={item.href}
              {...item}
              isActive={location.pathname === item.href}
              onClick={() => handleNavigation(item.href)}
            />
          ))}

          {/* Sign out button */}
          <div className="pt-4 mt-4 border-t border-tiber-200">
            <button
              onClick={handleSignOut}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              <span className="font-medium">{t("common.signOut")}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Sidebar - Mobile */}
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSidebarOpen(false)}
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
            />

            {/* Sidebar */}
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-64 bg-white shadow-2xl z-50 overflow-y-auto"
            >
              <div className="p-4">
                {/* Close Button */}
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center space-x-2">
                    <ShieldCheckIcon className="w-6 h-6 text-tiber-500" />
                    <span className="text-lg font-display font-bold text-juridico">
                      {t("admin.header.title")}
                    </span>
                  </div>
                  <button
                    onClick={() => setIsSidebarOpen(false)}
                    className="p-2 rounded-lg hover:bg-nieve transition-colors"
                  >
                    <XMarkIcon className="w-6 h-6 text-juridico" />
                  </button>
                </div>

                {/* Admin Badge */}
                <div className="mb-4">{getAdminRoleBadge()}</div>

                {/* Items */}
                <div className="space-y-2">
                  {sidebarItems.map((item) => (
                    <SidebarItem
                      key={item.href}
                      {...item}
                      isActive={location.pathname === item.href}
                      onClick={() => handleNavigation(item.href)}
                    />
                  ))}

                  {/* Sign out button */}
                  <div className="pt-4 mt-4 border-t border-tiber-200">
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <ArrowRightOnRectangleIcon className="w-5 h-5" />
                      <span className="font-medium">{t("common.signOut")}</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 min-h-screen">
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
